import { ORDER_TRACKING_LABELS, type OrderStatus, type OrderView } from '@khaddi/contracts';
import { Avatar, brandSurface, Button, colors, ErrorNote, formatDateTime, formatTime, formatVnd, Icon, Screen, Spinner, Text, useApi, useAsync, useLayout, useRouter } from '@khaddi/ui';
import { useEffect, useState } from 'react';
import { Linking, Pressable, View } from 'react-native';
import { InlineError } from '../components/bits';
import { MapIllustration } from '../components/MapIllustration';
import { useTabNav } from '../components/TabNav';
import { openPayment, paymentUrlFor } from '../state/payments';

const POLL_MS = 5000;

/** Progress rank; both shipper states share a step. */
const RANK: Record<OrderStatus, number> = {
  pending_payment: 0,
  new: 1,
  brewing: 2,
  awaiting_shipper: 3,
  shipper_assigned: 3,
  delivering: 4,
  completed: 5,
  cancelled: -1,
};

type StepState = 'done' | 'now' | 'todo';
type Step = { label: string; time: string; state: StepState };

const PAY_ON_RECEIPT = { cod: 'Đã đặt đơn · trả tiền khi nhận', cash: 'Đã đặt đơn · trả tại quầy' } as const;

/** The API suffixes paid labels with "✓"; the timeline already says whether it was paid. */
const paidLabel = (order: OrderView): string => {
  if (order.paymentMethod === 'cod' || order.paymentMethod === 'cash') return PAY_ON_RECEIPT[order.paymentMethod];
  const method = order.paymentLabel.replace(/\s*✓$/, '');
  return order.paymentStatus === 'unpaid' ? `Thanh toán qua ${method}` : `Đã thanh toán qua ${method}`;
};

/** The shared tracking wording assumes prepaid orders; COD orders haven't paid yet. */
const statusText = (order: OrderView): string =>
  order.status === 'new' && (order.paymentMethod === 'cod' || order.paymentMethod === 'cash') ? 'Đã đặt đơn, chờ quán nhận' : ORDER_TRACKING_LABELS[order.status];

/**
 * Timeline entries from the API plus the steps still ahead, greyed out.
 * The latest reached step is "now" until the order completes.
 */
const buildSteps = (order: OrderView): Step[] => {
  const delivery = order.channel === 'app_delivery';
  const reached = Math.max(0, ...order.timeline.map((t) => RANK[t.status]));
  const timeAt = (rank: number): string => {
    const entries = order.timeline.filter((t) => RANK[t.status] === rank);
    const last = entries[entries.length - 1];
    return last ? formatTime(last.at) : '—';
  };
  const plan: { rank: number; label: string }[] = delivery
    ? [
        { rank: 1, label: paidLabel(order) },
        { rank: 2, label: ORDER_TRACKING_LABELS.brewing },
        { rank: 3, label: reached === 3 ? ORDER_TRACKING_LABELS[order.status] : 'Shipper đã lấy hàng' },
        { rank: 4, label: ORDER_TRACKING_LABELS.delivering },
        { rank: 5, label: ORDER_TRACKING_LABELS.completed },
      ]
    : [
        { rank: 1, label: paidLabel(order) },
        { rank: 2, label: ORDER_TRACKING_LABELS.brewing },
        { rank: 5, label: 'Đã nhận món tại quán' },
      ];
  const completed = order.status === 'completed';
  const current = plan.reduce((at, step, index) => (step.rank <= reached ? index : at), -1);
  return plan.map(({ rank, label }, index) => ({
    label,
    time: rank <= reached ? timeAt(rank) : '—',
    state: index < current || (completed && index === current) ? 'done' : index === current ? 'now' : 'todo',
  }));
};

const minutesUntil = (iso: string | null, now: number): number | null => {
  if (!iso) return null;
  const minutes = Math.round((new Date(iso).getTime() - now) / 60000);
  return minutes > 0 ? minutes : null;
};

/** Re-renders every 30 s so the "N phút" countdown stays honest between polls. */
const useNow = (): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
};

/** Keeps the phone screen on while the customer waits at the counter, so the "ready" chime can play. */
const useKeepScreenOn = (on: boolean) => {
  useEffect(() => {
    if (!on || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let released = false;
    const acquire = () => {
      if (document.visibilityState !== 'visible') return;
      navigator.wakeLock.request('screen').then(
        (l) => {
          if (released) void l.release();
          else lock = l;
        },
        () => undefined,
      );
    };
    // the lock drops whenever the page is hidden; take it again on return
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', acquire);
      void lock?.release();
    };
  }, [on]);
};

const WAITING_STATUSES = new Set(['new', 'brewing']);

export const TrackingScreen = ({ orderId }: { orderId: string }) => {
  const api = useApi();
  const { navigate } = useRouter();
  const { data: order, error, loading, reload } = useAsync(() => api.orders.get(orderId), [api, orderId], { pollMs: POLL_MS });
  const { wide } = useLayout();
  const nav = useTabNav('orders', { wideOnly: true });
  useKeepScreenOn(order?.channel === 'app_pickup' && WAITING_STATUSES.has(order.status));

  if (loading && !order) return <Spinner />;
  if (!order) return <ErrorNote message={error ?? 'Không tìm thấy đơn hàng'} onRetry={() => void reload()} />;

  const delivery = order.channel === 'app_delivery';
  const content = (
    <View style={{ flex: 1, minHeight: '100vh' as unknown as number, backgroundColor: colors.border }}>
      <View style={{ height: delivery ? 380 : 240, overflow: 'hidden' }}>
        {delivery ? <MapIllustration /> : <PickupBanner order={order} />}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Về trang chủ"
          onPress={() => navigate('/')}
          style={{ position: 'absolute', top: 16, left: 16, width: 44, height: 44, borderRadius: 22, backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(28, 69, 95,.12)' }}
        >
          <Icon name="chevronLeft" size={22} color={colors.text} strokeWidth={2.2} />
        </Pressable>
        {delivery ? (
          <View style={{ position: 'absolute', top: 16, right: 16, height: 44, borderRadius: 22, paddingHorizontal: 14, backgroundColor: '#FFFFFF', justifyContent: 'center', boxShadow: '0 2px 8px rgba(28, 69, 95,.12)' }}>
            <Text variant="bodyStrong" style={{ fontSize: 13 }}>
              #{order.code}
            </Text>
          </View>
        ) : null}
      </View>
      <OrderSheet order={order} onReload={() => void reload()} />
    </View>
  );
  // Phones keep the full-bleed header with its own back button; wide screens add the main nav around it.
  return wide ? (
    <Screen nav={nav} background={colors.border} contentStyle={{ paddingBottom: 0 }}>
      {content}
    </Screen>
  ) : (
    content
  );
};

/** In-store orders have no route to show: the banner names the store and the code to read to the cashier. */
const PickupBanner = ({ order }: { order: OrderView }) => (
  <View style={{ ...brandSurface, flex: 1, alignItems: 'center', justifyContent: 'center', gap: 6, paddingHorizontal: 24, paddingTop: 40 }}>
    <View style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: 'rgba(255,255,255,.55)', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name="store" size={26} color={colors.text} />
    </View>
    <Text variant="caption" color={colors.text}>
      Nhận món tại quầy · {order.branch.name}
    </Text>
    <Text variant="display" numeric style={{ fontSize: 40, lineHeight: 44 }}>
      #{order.code}
    </Text>
    <Text variant="caption" color={colors.text} style={{ textAlign: 'center' }}>
      Đọc mã đơn cho thu ngân khi nhận món
    </Text>
  </View>
);

const OrderSheet = ({ order, onReload }: { order: OrderView; onReload: () => void }) => {
  const now = useNow();
  const { navigate } = useRouter();
  const delivery = order.channel === 'app_delivery';
  const minutes = minutesUntil(order.estimatedAt, now);
  const steps = buildSteps(order);
  const currentLabel = statusText(order);
  const paymentUrl = order.status === 'pending_payment' ? paymentUrlFor(order.id) : null;

  const headline =
    order.status === 'completed'
      ? { caption: delivery ? 'Đã giao lúc' : 'Hoàn tất lúc', value: formatTime(order.updatedAt) }
      : { caption: delivery ? 'Dự kiến giao lúc' : 'Dự kiến xong lúc', value: order.estimatedAt ? formatTime(order.estimatedAt) : '—' };

  return (
    <View style={{ flex: 1, marginTop: -50, backgroundColor: '#FFFFFF', borderTopLeftRadius: 26, borderTopRightRadius: 26, paddingTop: 10, paddingHorizontal: 20, paddingBottom: 24, gap: 16 }}>
      <View style={{ width: 40, height: 5, borderRadius: 3, backgroundColor: colors.border, alignSelf: 'center' }} />

      {order.status === 'cancelled' ? (
        <View style={{ backgroundColor: colors.redSoft, borderRadius: 18, padding: 14, gap: 4 }}>
          <Text variant="title" color={colors.redText}>
            Đơn đã hủy
          </Text>
          <Text variant="caption" color={colors.redText}>
            {order.cancelReason ? `Lý do: ${order.cancelReason}` : 'Đơn hàng này đã được hủy.'}
            {order.paymentStatus === 'refunded' ? ' · Tiền đã được hoàn về phương thức thanh toán.' : ''}
          </Text>
          <Button label="Đặt lại món" variant="outline" size="md" onPress={() => navigate('/menu')} style={{ marginTop: 8, alignSelf: 'flex-start' }} />
        </View>
      ) : order.status === 'pending_payment' ? (
        <View style={{ backgroundColor: colors.goldSoft, borderRadius: 18, padding: 14, gap: 6 }}>
          <Text variant="title" color={colors.goldText}>
            Đang chờ thanh toán
          </Text>
          <Text variant="caption" color={colors.goldText}>
            Quán sẽ bắt đầu pha khi nhận được {formatVnd(order.pricing.total)} qua {order.paymentLabel}.
          </Text>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
            {paymentUrl ? (
              <Button
                label="Thanh toán lại"
                size="md"
                onPress={() => openPayment(paymentUrl, order.id)}
              />
            ) : null}
            <Button label="Kiểm tra lại" size="md" variant="outline" onPress={onReload} />
          </View>
          {!paymentUrl ? <InlineError message={'Mở lại cổng thanh toán từ thiết bị đã đặt đơn, hoặc liên hệ quán.'} /> : null}
        </View>
      ) : (
        <View>
          <Text variant="caption" style={{ fontSize: 13 }}>
            {headline.caption}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <Text variant="display" numeric style={{ fontSize: 34, lineHeight: 36 }}>
              {headline.value}
            </Text>
            <Text variant="body" color={colors.primaryText} style={{ fontWeight: '600' }}>
              {order.status === 'completed' ? currentLabel : minutes ? `${currentLabel} · ${minutes} phút` : currentLabel}
            </Text>
          </View>
        </View>
      )}

      {order.shipper ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: colors.background, borderRadius: 18, padding: 12 }}>
          <Avatar initials={order.shipper.initials} />
          <View style={{ flex: 1 }}>
            <Text variant="heading">{order.shipper.name}</Text>
            <Text variant="caption">
              {[order.shipper.vehicle, order.shipper.plate, `★ ${order.shipper.rating.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}`].filter(Boolean).join(' · ')}
            </Text>
          </View>
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Nhắn tin shipper"
            onPress={() => void Linking.openURL(`sms:${order.shipper?.phone ?? ''}`)}
            style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center' }}
          >
            <Icon name="message" size={20} color={colors.primaryText} />
          </Pressable>
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Gọi shipper"
            onPress={() => void Linking.openURL(`tel:${order.shipper?.phone ?? ''}`)}
            style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' }}
          >
            <Icon name="phone" size={20} color={colors.onPrimary} />
          </Pressable>
        </View>
      ) : null}

      {order.status !== 'cancelled' ? (
        <View accessibilityRole="list">
          {steps.map((step, index) => (
            <TimelineStep key={step.label} step={step} last={index === steps.length - 1} />
          ))}
        </View>
      ) : null}

      <OrderSummary order={order} />

      <View style={{ flexDirection: 'row', gap: 8 }}>
        {order.status !== 'cancelled' && order.pricing.pointsToEarn > 0 ? (
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.goldSoft, borderRadius: 14, paddingVertical: 10, paddingHorizontal: 12 }}>
            <Icon name="star" size={18} color={colors.goldDeep} filled />
            <Text variant="caption" color={colors.goldText} style={{ flex: 1 }}>
              <Text variant="caption" color={colors.goldText} style={{ fontWeight: '700' }}>
                +{order.pricing.pointsToEarn} điểm
              </Text>{' '}
              {order.status === 'completed' ? 'đã cộng vào tài khoản' : delivery ? 'cộng khi nhận hàng' : 'cộng khi nhận món'}
            </Text>
          </View>
        ) : null}
        {order.invoice ? (
          <Pressable
            accessibilityRole="link"
            onPress={() => void Linking.openURL(order.invoice?.lookupUrl ?? '')}
            style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.surfaceMuted, borderRadius: 14, paddingVertical: 10, paddingHorizontal: 12, minHeight: 44 }}
          >
            <Icon name="file" size={18} color={colors.primaryText} />
            <Text variant="caption" color={colors.text} style={{ fontWeight: '600', flex: 1 }}>
              Xem hóa đơn điện tử
            </Text>
          </Pressable>
        ) : order.invoiceRequested && order.status !== 'cancelled' ? (
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.surfaceMuted, borderRadius: 14, paddingVertical: 10, paddingHorizontal: 12 }}>
            <Icon name="file" size={18} color={colors.textMuted} />
            <Text variant="caption" style={{ flex: 1 }}>
              Hóa đơn điện tử phát hành khi đơn hoàn tất
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
};

const TimelineStep = ({ step, last }: { step: Step; last: boolean }) => (
  <View accessibilityRole="listitem" style={{ flexDirection: 'row', gap: 12, minHeight: 44 }}>
    <View style={{ width: 22, alignItems: 'center' }}>
      <View
        style={{
          width: 22,
          height: 22,
          borderRadius: 11,
          borderWidth: 3,
          borderColor: step.state === 'todo' ? colors.borderStrong : colors.primaryText,
          backgroundColor: step.state === 'done' ? colors.primary : '#FFFFFF',
        }}
      />
      {!last ? <View style={{ width: 2, flex: 1, backgroundColor: step.state === 'done' ? colors.primary : colors.border }} /> : null}
    </View>
    <View style={{ paddingBottom: 10, flex: 1 }}>
      <Text variant="body" color={step.state === 'todo' ? colors.textMuted : colors.text} style={{ fontWeight: step.state === 'now' ? '700' : '500' }}>
        {step.label}
      </Text>
      <Text variant="caption">{step.time}</Text>
    </View>
  </View>
);

/** Collapsed receipt under the timeline: what was ordered and where it goes. */
const OrderSummary = ({ order }: { order: OrderView }) => {
  const [open, setOpen] = useState(false);
  return (
    <View style={{ backgroundColor: colors.background, borderRadius: 18, paddingHorizontal: 14 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        onPress={() => setOpen((o) => !o)}
        style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 52 }}
      >
        <Icon name="receipt" size={20} color={colors.primaryText} />
        <View style={{ flex: 1 }}>
          <Text style={{ fontWeight: '600' }} numberOfLines={1}>
            {order.itemsSummary}
          </Text>
          <Text variant="label" style={{ fontWeight: '400' }}>
            {formatDateTime(order.createdAt)} · {order.paymentLabel}
          </Text>
        </View>
        <Text variant="bodyStrong" numeric>
          {formatVnd(order.pricing.total)}
        </Text>
        <View style={{ transform: [{ rotate: open ? '180deg' : '0deg' }] }}>
          <Icon name="chevronDown" size={18} color={colors.textMuted} />
        </View>
      </Pressable>
      {open ? (
        <View style={{ paddingBottom: 12, gap: 8 }}>
          {order.lines.map((line, i) => (
            <View key={`${line.productId}-${i}`} style={{ flexDirection: 'row', gap: 8 }}>
              <View style={{ flex: 1 }}>
                <Text variant="body">
                  {line.quantity}× {line.productName}
                </Text>
                <Text variant="label" style={{ fontWeight: '400' }}>
                  {line.optionsLabel}
                </Text>
              </View>
              <Text numeric>{formatVnd(line.lineTotal)}</Text>
            </View>
          ))}
          <View style={{ height: 1, backgroundColor: colors.border }} />
          <Text variant="caption">
            {order.delivery ? `Giao đến ${order.delivery.recipientName} · ${order.delivery.phone} · ${order.delivery.address}` : `Nhận tại ${order.branch.name} · ${order.branch.address}`}
          </Text>
          {order.pricing.deliveryFee ? <Text variant="caption">Phí giao hàng {formatVnd(order.pricing.deliveryFee)}</Text> : null}
          {order.pricing.promotionDiscount ? <Text variant="caption">{order.pricing.promotionName ?? 'Khuyến mãi'} −{formatVnd(order.pricing.promotionDiscount)}</Text> : null}
          {order.pricing.pointsDiscount ? <Text variant="caption">Đổi {order.pricing.pointsRedeemed} điểm −{formatVnd(order.pricing.pointsDiscount)}</Text> : null}
        </View>
      ) : null}
    </View>
  );
};
